import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import './App.css';
import Footer from './Footer';
import Navbar from './Navbar';
import AdminPage from './AdminPage';
import AboutPage from './AboutPage';
import ContactPage from './ContactPage';
import ProductsPage from './ProductsPage';
import ProductDetailPage from './ProductDetailPage';
import CartPage from './CartPage';
import CheckoutPage from './CheckoutPage';
import PrivacyPolicyPage from './PrivacyPolicyPage';
import TermsOfServicePage from './TermsOfServicePage';
import { apiRequest } from './authConfig';
import { LICENSE_MANAGEMENT_API_ADDRESS } from './apiConfig';

function Home() {
  return (
    <>
      <header className="App-header">
        <img src="/images/logo.svg" className="App-logo" alt="logo" />
        <p><b>Nuvolem Software Solutions</b></p>
      </header>
      <div className="App-body">
        <p>
          We are a startup working to develop applications for AEC productivity.
        </p>
        <p>
          Please check back to hear about applications that let BIM admins manage the firm-wide apps and configurations, improve productivity and centralize content management.
        </p>
      </div>
    </>
  );
}

function App() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null); // null = not yet fetched

  const activeAccountId = (instance.getActiveAccount() || accounts[0])?.localAccountId ?? null;

  // A login started from a gated page (e.g. checkout) carries its return path in
  // MSAL's `state`. The redirect otherwise drops the user on home, so restore
  // that path once the redirect resolves. handleRedirectPromise is memoized by
  // MSAL and keeps returning that same result, and `navigate` changes identity
  // on every navigation, so this effect re-runs — the ref guard ensures we act
  // on the returned state exactly once and don't trap the user on that page.
  const redirectHandled = useRef(false);
  useEffect(() => {
    if (redirectHandled.current) return;
    let cancelled = false;
    instance.handleRedirectPromise().then(result => {
      const returnTo = result?.state;
      if (!cancelled && !redirectHandled.current &&
          typeof returnTo === 'string' && returnTo.startsWith('/')) {
        redirectHandled.current = true;
        navigate(returnTo, { replace: true });
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [instance, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !activeAccountId) {
      setOrders(null);
      return;
    }
    setOrders(null);
    let cancelled = false;
    const fetchOrders = async () => {
      const account = instance.getActiveAccount() || accounts[0];
      try {
        const { accessToken } = await instance.acquireTokenSilent({
          scopes: apiRequest.scopes,
          account
        });
        const res = await fetch(`${LICENSE_MANAGEMENT_API_ADDRESS}?operation=get_orders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (!res.ok) { if (!cancelled) setOrders([]); return; }
        const data = await res.json();
        if (!cancelled) setOrders(Array.isArray(data) ? data : []);
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({ scopes: apiRequest.scopes, account });
          return;
        }
        if (!cancelled) setOrders([]);
      }
    };
    fetchOrders();
    return () => { cancelled = true; };
  }, [isAuthenticated, activeAccountId, instance, accounts]);

  const isAdmin = Array.isArray(orders) && orders.length > 0;

  return (
    <div className="App">
      <Navbar isAdmin={isAdmin} fetchingUserInfo={isAuthenticated && orders === null} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route
          path="/admin"
          element={
            !isAuthenticated ? <Navigate to="/" replace /> :
            orders === null ? <div className="loading-area">Loading…</div> :
            !isAdmin ? <Navigate to="/" replace /> :
            <AdminPage orders={orders} />
          }
        />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/products/:productId" element={<ProductDetailPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer />
    </div>
  );
}

export default App;
